import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { EncryptInternalResponse, GetDecryptStringResponse, GetSharedRedirectResponse } from '../types/common';
import { keyFactory } from '../util/keyFactory';
import { logger } from '../util/logger';
import { HtmlToExcelClient, HttpClient } from './axiosConfig';

// Service to get jurisdiction definitions
export const useGetDecryptString = (encryptedString: string) => {  
  return useQuery({
    queryKey: [keyFactory.getDecryptString, encryptedString],
    queryFn: async () => {

      const result = await HttpClient.get<GetDecryptStringResponse>(
        `commons/decrypt-string?params=${encryptedString}`
      );
      return result.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!encryptedString,
  });
};

export const useEncryptInternalMutation = () => {
  return useMutation({
    mutationFn: async (input: string) => {
      const normalized = input?.trim();
      const response = await HttpClient.get<EncryptInternalResponse>('commons/encrypt-internal', {
        params: { input: normalized },
      });
      const encryptedValue = response.data?.data?.trim() ?? '';
      return encryptedValue;
    },
  });
};

export function isPlainNumericProductId(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  return !!trimmed && /^\d+$/.test(trimmed);
}

/** True for a single numeric id or comma-separated numeric ids (e.g. `107` or `107,108`). */
export function needsProductIdEncryption(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  if (isPlainNumericProductId(trimmed)) return true;
  return trimmed.includes(',') && trimmed.split(',').every((part) => /^\d+$/.test(part.trim()));
}

export async function encryptProductIdForApi(value: string): Promise<string> {
  const trimmed = value.trim();
  if (!trimmed || !needsProductIdEncryption(trimmed)) {
    return trimmed;
  }

  const encryptedParts = await Promise.all(
    trimmed.split(',').map(async (part) => {
      const id = part.trim();
      const response = await HttpClient.get<EncryptInternalResponse>('commons/encrypt', {
        params: { input: id },
      });
      return response.data?.data?.trim() || id;
    }),
  );
  return encryptedParts.join(',');
}

export const useGetEncryptedProductId = (plainProductId: string | undefined) => {
  return useQuery({
    queryKey: ['encryptProductId', plainProductId],
    queryFn: () => encryptProductIdForApi(plainProductId!),
    enabled: !!plainProductId && needsProductIdEncryption(plainProductId),
  });
};

// Non-hook helper for shared redirect, safe to use outside React components/hooks
export const getSharedRedirect = async (encryptedUrl: string) => {
  const result = await HttpClient.get<GetSharedRedirectResponse>(
    `commons/shared-redirect?eUrl=${encryptedUrl}`
  );
  return result.data;
};

// Service to get jurisdiction definitions
export const useGetDownloadPdf = (documentID: string, path: string, productionID: string, opinionID: string, location: string, pageID: string, page: string, gridID: string, renStyleId: string) => {  
  return useQuery<GetSharedRedirectResponse['data']>({
    queryKey: [keyFactory.getDownloadPdf, documentID, path, productionID, opinionID, location, pageID, page, gridID, renStyleId],
    queryFn: async () => {
      const url = `storage/pdf-page-download?json=true&docID=${documentID}&path=${path}&prodId=${productionID}${page ? `&page=${page}` : ''}${opinionID ? `&opinionID=${opinionID}` : ''}${gridID ? `&gridID=${gridID}` : ''}${pageID ? `&pageID=${pageID}` : ''}&location=${location}${renStyleId ? `&erenStyleId=${renStyleId}` : ''}`;
      const result = await HttpClient.get<GetSharedRedirectResponse>(url);
      return result.data.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!documentID,
  });
};

export const useGetPrivateBankingDocumentPermission = (productId: string) => {
  return useQuery<{ isEnabled: boolean; features: Array<{ featureId: number; isEnabled: boolean }> }>({
    queryKey: [keyFactory.getPrivateBankingDocPermission, productId],
    queryFn: async () => {
      const result = await HttpClient.get(`storage/product/${productId}/doc-permission`);
      return result.data.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!productId,
  });
};

export const useGetDownloadViewDocument = (filename: string, opinionID?: number, path?: string, type?: string, compare?: number, location?: string, contentTileName?: string, part?: string, viewResource?: number, productionId?: string, pageId?: number, gridID?: number, renStyleID?: string, plainProductionId?: string, alertId?: string, isListofLink?: number, renStyleId?: number, isfeatureItem?: number, queryOptions?: { enabled?: boolean }) => {
  return useQuery<GetSharedRedirectResponse['data']>({
    queryKey: [
      keyFactory.getDownloadViewDoc, filename, opinionID, path, type, compare, location, contentTileName, part, viewResource, productionId, pageId, gridID, renStyleID, plainProductionId, alertId, isListofLink, renStyleId, isfeatureItem,
    ],
    queryFn: async () => {
      const encId = productionId ?? renStyleID ?? (renStyleId == null ? undefined : String(renStyleId));
      const productionId_ = plainProductionId ?? (renStyleId == null ? undefined : String(renStyleId));
      const url = `/storage/view-doc?json=true&filename=${filename}${path ? `&path=${path}` : ''}${opinionID ? `&opinionID=${opinionID}` : ''}${type ? `&type=${type}` : ''}${compare ? `&compare=${compare}` : ''}${location ? `&location=${location}` : ''}${contentTileName ? `&contentTileName=${contentTileName}` : ''}${part ? `&part=${part}` : ''}${viewResource ? `&viewResource=${viewResource}` : ''}${pageId ? `&pageId=${pageId}` : ''}${gridID ? `&gridID=${gridID}` : ''}${encId ? `&erenStyleId=${encId}` : ''}${productionId_ ? `&prodId=${productionId_}` : ''}${productionId_ ? `&renStyleId=${productionId_}` : ''}${alertId ? `&alertId=${alertId}` : ''}${isListofLink ? `&isListofLink=${isListofLink}` : ''}${isfeatureItem ? `&isfeatureItem=${isfeatureItem}` : ''}`;
      const result = await HttpClient.get(url);
      return result.data.data;
    },
    placeholderData: keepPreviousData,
    enabled: (queryOptions?.enabled ?? true) && !!filename,
  });
};

/** Named-params wrapper around `useGetDownloadViewDocument` (avoids long `undefined` call sites). */
export type DownloadViewDocumentQueryParameters = {
  filename: string;
  opinionID?: number;
  path?: string;
  type?: string;
  compare?: number;
  location?: string;
  contentTileName?: string;
  part?: string;
  viewResource?: number;
  productionId?: string;
  pageId?: number;
  gridID?: number;
  renStyleID?: string;
  plainProductionId?: string;
  alertId?: string;
  isListofLink?: number;
  renStyleId?: number;
  isfeatureItem?: number;
  enabled?: boolean;
};

export const useDownloadViewDocumentQuery = (
  parameters: DownloadViewDocumentQueryParameters
) =>
  useGetDownloadViewDocument(
    parameters.filename,
    parameters.opinionID,
    parameters.path,
    parameters.type,
    parameters.compare,
    parameters.location,
    parameters.contentTileName,
    parameters.part,
    parameters.viewResource,
    parameters.productionId,
    parameters.pageId,
    parameters.gridID,
    parameters.renStyleID,
    parameters.plainProductionId,
    parameters.alertId,
    parameters.isListofLink,
    parameters.renStyleId,
    parameters.isfeatureItem,
    { enabled: parameters.enabled },
  );

/** Page reference document download (docID, page, ProdID only – mirrors CF downloadResource.cfm logic). */
export const useGetDownloadResource = (documentID: string, page: string, productionID: string) => {
  return useQuery<GetSharedRedirectResponse['data']>({
    queryKey: [keyFactory.getDownloadResource, documentID, page, productionID],
    queryFn: async () => {
      const url = `/storage/pdf-resource-download?json=true&docID=${encodeURIComponent(documentID)}&page=${encodeURIComponent(page)}&prodId=${encodeURIComponent(productionID)}`;
      const result = await HttpClient.get<GetSharedRedirectResponse>(url);
      return result.data.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!documentID && !!productionID,
  });
};

export const useGetViewCkEditorDocument = (documentID: string, path: string, productionID: string, opinionID: string, location: string, page: string,pageID?:string,gridID?:string) => {  
  return useQuery<GetSharedRedirectResponse['data']>({
    queryKey: [keyFactory.getViewCkEditorDocument, documentID, path, productionID, opinionID, location, page,pageID,gridID],
    queryFn: async (): Promise<GetSharedRedirectResponse['data']> => {
      const queryParameters: Array<string> = [`docID=${documentID}`];
      
      if (page) {
        queryParameters.push(`page=${page}`);
      }
      
      if (path) {
        queryParameters.push(`path=${path}`);
      }
      
      if (productionID) {
        queryParameters.push(`ProdID=${productionID}`, `erenStyleId=${productionID}`);
      }
      
      if (opinionID) {
        queryParameters.push(`opinionID=${opinionID}`);
      }
      if (pageID) {
        queryParameters.push(`pageID=${pageID}`);
      }
      if (location) {
        queryParameters.push(`location=${location}`);
      }
      if(gridID){
        queryParameters.push(`gridID=${gridID}`);
      }
      
      const url = `storage/view-ck-editor-doc?json=true&${queryParameters.join('&')}`;
      const result = await HttpClient.get<GetSharedRedirectResponse>(url);
      
      // Try to extract Content-Disposition from XHR if available (before CORS filtering)
      // This is a workaround for CORS issues
      try {
        const allHeaders = (result as any).request?.getAllResponseHeaders?.();
        const cdMatch = allHeaders?.match(/content-disposition:\s*(.+)/i);
        if (cdMatch?.[1]) {
          const contentDisposition = cdMatch[1].trim();
          // Store it in response headers for later access
          if (result.headers) {
            (result.headers as any)['content-disposition'] = contentDisposition;
          }
        }
      } catch (error) {
        logger.warn('Could not access XHR headers:', error);
      }
      
      return result.data.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!documentID,
  });
};

//const result = await HttpClient.get(`/storage/view?path=/pageTopBannerImage/505/157836.jpg`),

export const useGetBannerImage = (path: string) => {
  return useQuery({
    queryKey: [keyFactory.getBannerImage, path], // Include path in queryKey for proper caching
    queryFn: async () => {
      const result = await HttpClient.get(
        `storage/view?path=${path}`, // Use dynamic path parameter
        { responseType: 'blob' }
      );
      
      // Convert blob to object URL for image display
      const imageUrl = URL.createObjectURL(result.data);
      return imageUrl;
    },
    placeholderData: keepPreviousData,
    enabled: !!path,
  });
};

interface ExportMediatorPayload {
  prefix: string;
  fileName: string;
}

// Service to export excel mediator user click on download button then before export api call this api will be called
export const useExportMediator = () => {
  return useMutation({
    mutationFn: async (dataToSend: ExportMediatorPayload) => {
      const response = await HtmlToExcelClient.post<Blob>(
        `/users/download`,
        {
          prefix: dataToSend.prefix,
          fileName: dataToSend.fileName,
        },
        {
          responseType: 'blob',
        }
      );

      return response;
    },
  });
};

